"use client";

import { useEffect } from "react";

import { identifyPosthogUser } from "@/lib/posthog-events";
import { isInternalEmail } from "@/lib/internal-users";

interface PostHogIdentityProps {
  /** Internal users.id — the stable distinct_id. */
  id: string;
  email: string;
  isAdmin: boolean;
}

/**
 * Renders nothing; ties PostHog events to the logged-in user. Mount it on every
 * authenticated surface where the user is known — inside ProfileMenu (instance /
 * billing / onboarding headers + logged-in marketing nav) and directly on authed
 * pages/layouts that don't render ProfileMenu (e.g. /account-inactive and the /admin
 * layout, whose own shell has no ProfileMenu). No-op until the SDK is ready and
 * analytics consent holds; de-duped by id in lib/posthog-events so multiple
 * mounts identify once.
 *
 * WHAT WE SEND, AND WHAT WE DELIBERATELY DO NOT. `distinct_id` is the internal
 * `users.id` — stable, meaningless outside our database, and enough to stitch a
 * funnel. Email and name used to ride along as person properties; they don't any
 * more. They turned every PostHog person into a customer record held by a third
 * party, for no analytical gain: nothing we ask PostHog is answered by a name,
 * and the id joins back to our own tables when a question genuinely needs one.
 *
 * `is_admin` stays — it segments internal from customer behaviour and identifies
 * nobody on its own. The `internal` flag is computed here and never sent; it is
 * used locally to keep insiders out of insights.
 */
export function PostHogIdentity({ id, email, isAdmin }: PostHogIdentityProps) {
  useEffect(() => {
    identifyPosthogUser(
      id,
      { is_admin: isAdmin },
      { internal: isInternalEmail(email) },
    );
  }, [id, email, isAdmin]);

  return null;
}
